import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Avatar,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  Snackbar
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from '@mui/icons-material';

/**
 * Componente para upload de foto do usuário
 */
const UserPhotoUpload = ({ userId, currentPhoto, onPhotoChange, size = 120 }) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);

  const [isUploading, setIsUploading] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(currentPhoto);
  const [showEditMode, setShowEditMode] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Atualizar previewPhoto quando currentPhoto mudar (apenas se não estiver em modo de edição)
  useEffect(() => {
    if (!showEditMode) {
      setPreviewPhoto(currentPhoto || null);
    }
  }, [currentPhoto, showEditMode]);

  // Obter inicial do usuário
  const getUserInitial = useCallback(() => {
    if (!userId) return 'U';
    if (typeof userId === 'string' && userId.length > 0) {
      return userId.charAt(0).toUpperCase();
    }
    if (typeof userId === 'number') {
      return userId.toString().charAt(0);
    }
    return 'U';
  }, [userId]);

  // Converter arquivo para base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Comprimir imagem
  const compressImage = async (file, maxWidth = 800, maxHeight = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(resolve, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Lidar com upload de arquivo
  const handleFileUpload = useCallback(async (file) => {
    if (!userId) {
      setError('Usuário não identificado');
      return;
    }

    try {
      setError(null);
      setIsUploading(true);

      // Validações
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Formato não suportado. Use JPG, PNG ou WebP');
      }

      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error('Arquivo muito grande. Máximo: 5MB');
      }

      // Comprimir imagem
      const compressedBlob = await compressImage(file, 800, 800, 0.7);
      const compressedBase64 = await fileToBase64(compressedBlob);

      // Preview
      setPreviewPhoto(compressedBase64);

      // Metadados
      const metadata = {
        fileName: file.name,
        originalSize: file.size,
        compressedSize: compressedBlob.size,
        uploadDate: new Date().toISOString(),
        mimeType: 'image/jpeg'
      };

      // Notificar componente pai
      if (onPhotoChange) {
        await onPhotoChange({
          photoUrl: compressedBase64,
          ...metadata
        });
      }

      setShowSuccess(true);
      setShowEditMode(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  }, [userId, onPhotoChange]);

  // Remover foto
  const handleRemovePhoto = useCallback(async () => {
    try {
      setPreviewPhoto(null);
      if (onPhotoChange) {
        await onPhotoChange(null);
      }
      setShowSuccess(true);
      setShowEditMode(false);
    } catch (err) {
      setError('Erro ao remover foto');
    }
  }, [onPhotoChange]);

  // Cancelar edição
  const handleCancelEdit = useCallback(() => {
    setPreviewPhoto(currentPhoto);
    setError(null);
    setShowEditMode(false);
  }, [currentPhoto]);

  // Abrir seletor de arquivo
  const handleOpenFileSelector = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Mudança de arquivo
  const handleFileChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    event.target.value = '';
  }, [handleFileUpload]);

  // Drag and drop
  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, [handleFileUpload]);

  const currentPhotoUrl = previewPhoto || currentPhoto;

  if (!userId) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Usuário não identificado
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'center' }}>
      {/* Container da foto */}
      <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
        <Avatar
          src={currentPhotoUrl}
          sx={{
            width: size,
            height: size,
            fontSize: size * 0.4,
            border: `3px solid ${theme.palette.primary.main}`,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
            cursor: showEditMode ? 'pointer' : 'default',
            transition: 'all 0.3s ease',
            '&:hover': showEditMode ? {
              transform: 'scale(1.05)',
              boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`
            } : {}
          }}
          onClick={showEditMode ? handleOpenFileSelector : undefined}
        >
          {!currentPhotoUrl && getUserInitial()}
        </Avatar>

        {/* Botão de edição */}
        {!showEditMode && (
          <IconButton
            onClick={() => setShowEditMode(true)}
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              border: `3px solid ${theme.palette.background.paper}`,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}
            size="small"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}

        {/* Loading overlay */}
        {isUploading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              borderRadius: '50%'
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
      </Box>

      {/* Modo de edição */}
      {showEditMode && (
        <Box sx={{ mt: 2 }}>
          {/* Área de drag and drop */}
          <Box
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleOpenFileSelector}
            sx={{
              border: `2px dashed ${theme.palette.primary.main}`,
              borderRadius: 2,
              p: 3,
              mb: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.02),
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05)
              }
            }}
          >
            <PhotoCameraIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Clique ou arraste uma foto aqui
            </Typography>
            <Typography variant="caption" color="text.secondary">
              JPG, PNG ou WebP • Máximo 5MB
            </Typography>
          </Box>

          {/* Botões de ação */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleRemovePhoto}
              disabled={isUploading || !currentPhotoUrl}
            >
              Remover
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={handleCancelEdit}
              disabled={isUploading}
            >
              Cancelar
            </Button>
          </Box>
        </Box>
      )}

      {/* Input de arquivo oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Mensagem de erro */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Snackbar de sucesso */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success">
          Foto atualizada com sucesso!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserPhotoUpload;


